import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getEnrichedProducts, getSamplePreview } from '../services/run-content.service';
import type { RunEnrichedProduct, SamplePreviewProduct } from '../types/run-content.api.types';

/** One page of a content-generation list, read for the "find one product" walk below. */
const FIND_PAGE_SIZE = 100;
/** Pages scanned before giving up — bounds the walk on a huge run; the journey drawer says so. */
const FIND_PAGE_CAP = 5;

/** The outcome of walking a content-generation list for one product's row. */
export interface ProductRowFind<T> {
  row: T | null;
  /** Rows actually scanned (for "based on the first N" wording when `truncated`). */
  scanned: number;
  /** The cap was hit before `row` was found or the list ran out — the figure may still exist later. */
  truncated: boolean;
}

/**
 * Neither the sample nor the enrichment stage has a per-product read (dossier confirmed none
 * exists) — only a paged "every product this stage touched" list. Walking it is fine for a
 * user-initiated, single-stage, single-product lookup (the journey drawer), unlike doing it once
 * per row of a list.
 */
async function findInPages<T>(
  fetchPage: (page: number) => Promise<{ items: T[]; total: number }>,
  matches: (row: T) => boolean,
): Promise<ProductRowFind<T>> {
  let scanned = 0;
  for (let page = 1; page <= FIND_PAGE_CAP; page++) {
    const data = await fetchPage(page);
    scanned += data.items.length;
    const row = data.items.find(matches);
    if (row) return { row, scanned, truncated: false };
    if (scanned >= data.total || data.items.length === 0)
      return { row: null, scanned, truncated: false };
  }
  return { row: null, scanned, truncated: true };
}

/** The sample stage's before/after row for one product — the journey drawer's Sample section. */
export function useProductSampleRow(runId: string, productId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runId, 'sample-find', { productId }),
    queryFn: () =>
      findInPages<SamplePreviewProduct>(
        (page) => getSamplePreview(runId, { page, size: FIND_PAGE_SIZE }),
        (r) => r.productId === productId,
      ),
    enabled: enabled && !!runId && !!productId,
  });
}

/**
 * The enrichment stage's field-level in→out row for one product — the journey drawer's section.
 * R1: one exact read (`enriched-products?productIds={id}`); an older content-gen ignores the
 * filter (its first row is then some other product), and only then are the pages walked.
 */
export function useProductEnrichedRow(runId: string, productId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.runContent(runId, 'enriched-find', { productId }),
    queryFn: async (): Promise<ProductRowFind<RunEnrichedProduct>> => {
      const exact = await getEnrichedProducts(runId, {
        page: 1,
        size: 1,
        productIds: [productId],
      });
      const first = exact.items[0];
      if (!first) return { row: null, scanned: 0, truncated: false };
      if (first.productId === productId) return { row: first, scanned: 1, truncated: false };
      return findInPages<RunEnrichedProduct>(
        (page) => getEnrichedProducts(runId, { page, size: FIND_PAGE_SIZE }),
        (r) => r.productId === productId,
      );
    },
    enabled: enabled && !!runId && !!productId,
  });
}
